// src/modules/admin/pages/AdminDashboard/index.js
import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useAdminData } from '../../hooks/useAdminData';
import { useAuth } from '../../../../shared/hooks/useAuth';
import AdminStatsCard from '../../components/AdminStatsCard';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import './styles.css';

const AdminDashboard = () => {
    const { stats, isLoading, error, refetch, lastFetched } = useAdminData();
    const { hasPermission } = useAuth();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await refetch();
        } finally {
            setIsRefreshing(false);
        }
    }, [refetch]);

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const formatTimestamp = () => {
        if (!lastFetched) return '';
        return `Last updated: ${lastFetched.toLocaleTimeString()}`;
    };

    if (isLoading) return <Loading isWarmup={false} />;
    if (error) return <ErrorState message={error} onRetry={handleRetry} />;
    if (!stats) return <ErrorState message="No data available" onRetry={handleRetry} />;

    // Map each admin link to its required permission(s) — any one match grants access
    const linkPermissions = {
        '/admin/competitions': ['competition:create', 'competition:edit'],
        '/admin/exemptions': ['activities:handleExemptions'],
        '/admin/users': ['users:managePermissions'],
    };

    const cardData = [
        {
            title: 'Total Users',
            value: stats.totalUsers ?? 0,
            icon: 'bi-people',
            subtitle: 'Registered accounts',
            link: '/admin/users',
            linkText: 'Manage',
        },
        {
            title: 'Active Competitions',
            value: stats.activeCompetitions ?? 0,
            icon: 'bi-trophy',
            subtitle: 'Currently running',
            link: '/admin/competitions',
            linkText: 'Manage',
        },
        {
            title: 'Upcoming Competitions',
            value: stats.upcomingCompetitions ?? 0,
            icon: 'bi-calendar-event',
            subtitle: 'Scheduled',
            link: '/admin/competitions',
            linkText: 'Manage',
        },
        {
            title: 'Pending Exemptions',
            value: stats.pendingExemptions ?? 0,
            icon: 'bi-exclamation-triangle',
            subtitle: 'Awaiting review',
            link: '/admin/exemptions',
            linkText: 'Review',
        },
    ].filter(card => {
        if (!card.link) return true;
        const required = linkPermissions[card.link];
        return !required || required.some(hasPermission);
    });

    return (
        <Container fluid className="admin-dashboard-container">
            <div className="admin-dashboard-header">
                <div>
                    <h1>Dashboard</h1>
                    <p className="header-subtitle">System overview and quick actions</p>
                </div>
                <div className="admin-dashboard-actions">
                    <span className="admin-dashboard-timestamp">{formatTimestamp()}</span>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="admin-dashboard-refresh-btn"
                    >
                        <i className={`bi bi-arrow-clockwise ${isRefreshing ? 'admin-dashboard-spin' : ''}`}></i>
                    </Button>
                </div>
            </div>

            <Row className="admin-dashboard-grid">
                {cardData.map((card) => (
                    <Col
                        key={card.title}
                        xs={12}
                        sm={6}
                        lg={4}
                        className="admin-dashboard-col"
                    >
                        <AdminStatsCard
                            title={card.title}
                            value={card.value}
                            icon={card.icon}
                            subtitle={card.subtitle}
                            link={card.link}
                            linkText={card.linkText}
                        />
                    </Col>
                ))}
            </Row>
        </Container>
    );
};

export default AdminDashboard;
